import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ConfirmationModal from '../../components/ConfirmationModal';
import Toast from '../../components/Toast';
import Spinner from '../../components/Spinner';
import './AdminProductEdit.css';

function AdminProductEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [product, setProduct] = useState(null);

  // Quill editor configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ]
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'link'
  ];

  // Modal states
  const [modal, setModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: '', // 'confirm', 'success', 'error'
    onConfirm: null
  });

  // Toast notifications
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    short_description: '',
    price: '',
    regular_price: '',
    sale_price: '',
    sku: '',
    stock_status: 'instock',
    featured: false,
    published: true,
    visibility: 'visible',
    category: '',
    tags: ''
  });

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get('/api/admin/products', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const productData = response.data.find(p => p.id === parseInt(id));

      if (!productData) {
        showToast('Product not found', 'error');
        setTimeout(() => navigate('/admin/products'), 1000);
        return;
      }

      setProduct(productData);
      setFormData({
        name: productData.name || '',
        description: productData.description || '',
        short_description: productData.short_description || '',
        price: productData.price || '',
        regular_price: productData.regular_price || '',
        sale_price: productData.sale_price || '',
        sku: productData.sku || '',
        stock_status: productData.stock_status || 'instock',
        featured: productData.featured === 1,
        published: productData.published === 1,
        visibility: productData.visibility || 'visible',
        category: productData.category || '',
        tags: productData.tags || ''
      });

      setLoading(false);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/login');
      } else {
        showToast('Failed to load product', 'error');
        setTimeout(() => navigate('/admin/products'), 1000);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: newValue
      };

      // Keep price field in sync with regular_price
      if (name === 'regular_price') {
        updated.price = newValue;
      }

      return updated;
    });
  };

  const handleSyncFromPrintify = () => {
    if (!product?.printify_product_id) return;

    // Show confirmation modal with details
    setModal({
      isOpen: true,
      title: 'Sync from Printify',
      message: (
        <div className="sync-details">
          <p>This will pull the latest product data from Printify.</p>
          <div className="info-message">
            <strong>What will be refreshed from Printify:</strong>
            <ul>
              <li>Product name and descriptions</li>
              <li>Prices (all variants)</li>
              <li>Stock status and availability</li>
              <li>All product images</li>
              <li>All product variants (colors/sizes)</li>
            </ul>
          </div>
          <div className="info-message" style={{marginTop: '8px'}}>
            <strong>What will NOT change (managed by you):</strong>
            <ul>
              <li>Categories</li>
              <li>Featured status</li>
              <li>Published/visibility status</li>
              <li>Sale price</li>
            </ul>
          </div>
        </div>
      ),
      type: 'confirm',
      onConfirm: async () => {
        closeModal();
        setSyncing(true);

        try {
          const token = localStorage.getItem('adminToken');
          await axios.post(`/api/webhooks/sync-from-printify/${id}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });

          // Show success modal
          setModal({
            isOpen: true,
            title: 'Sync Successful',
            message: 'Product has been synced from Printify successfully! The page will reload to show the updated data.',
            type: 'success',
            onConfirm: () => window.location.reload()
          });
        } catch (error) {
          const isStale = error.response?.data?.staleLink;
          const status = error.response?.status;
          let title = 'Sync Failed';
          let message;

          if (isStale || status === 404) {
            title = 'Product Not Found on Printify';
            message = 'This product no longer exists on Printify. It may have been deleted or recreated with a new ID. You can unlink it from Printify and manage it locally, or check the Printify dashboard.';
          } else if (status === 400) {
            title = 'Product Not Linked';
            message = 'This product is not linked to a Printify product. It can only be managed locally.';
          } else {
            message = `Failed to sync from Printify: ${error.response?.data?.error || error.message}`;
          }

          setModal({
            isOpen: true,
            title,
            message,
            type: 'error',
            onConfirm: closeModal
          });
          setSyncing(false);
        }
      }
    });
  };

  const closeModal = () => {
    setModal({
      isOpen: false,
      title: '',
      message: '',
      type: '',
      onConfirm: null
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('adminToken');

      // Step 1: Save locally
      await axios.put(`/api/admin/products/${id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      showToast('Product updated successfully!', 'success');

      setTimeout(() => navigate('/admin/products'), 1500);
    } catch (error) {
      showToast('Failed to update product: ' + (error.response?.data?.error || error.message), 'error');
      setSaving(false);
    }
  };

  if (loading) return <Spinner text="Loading product..." />;

  return (
    <div className="admin-product-edit">
      <div className="admin-header">
        <h1>Edit Product</h1>
        <div className="admin-nav">
          <button onClick={() => navigate('/admin/products')} className="btn-secondary">
            Back to Products
          </button>
          <button onClick={() => navigate('/admin/dashboard')} className="btn-secondary">
            Dashboard
          </button>
        </div>
      </div>

      {product?.printify_product_id && (
        <div className="printify-notice">
          <strong>Printify Product:</strong> Product data (name, description, images, variants, prices) syncs from Printify.
          <span> Categories, featured status, and published status are managed here.</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="edit-form">
        <div className="form-sections">
          {/* Left Column - Main Details */}
          <div className="form-section">
            <h2>Product Details</h2>

            <div className="form-group">
              <label htmlFor="name">Product Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="short_description">Short Description</label>
              <textarea
                id="short_description"
                name="short_description"
                value={formData.short_description}
                onChange={handleChange}
                rows={3}
                className="form-textarea"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Full Description</label>
              <ReactQuill
                theme="snow"
                value={formData.description}
                onChange={(content) => setFormData(prev => ({ ...prev, description: content }))}
                modules={quillModules}
                formats={quillFormats}
                className="quill-editor"
              />
            </div>

            <div className="form-group">
              <label htmlFor="sku">SKU</label>
              <input
                type="text"
                id="sku"
                name="sku"
                value={formData.sku}
                onChange={handleChange}
                className="form-input"
                disabled={product?.printify_product_id}
              />
              {product?.printify_product_id && (
                <small className="form-help">SKU is managed by Printify</small>
              )}
            </div>
          </div>

          {/* Right Column - Pricing & Status */}
          <div className="form-section">
            <h2>Pricing</h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="regular_price">Regular Price *</label>
                <div className="input-with-prefix">
                  <span className="input-prefix">$</span>
                  <input
                    type="number"
                    id="regular_price"
                    name="regular_price"
                    value={formData.regular_price}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    required
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="sale_price">Sale Price</label>
                <div className="input-with-prefix">
                  <span className="input-prefix">$</span>
                  <input
                    type="number"
                    id="sale_price"
                    name="sale_price"
                    value={formData.sale_price}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    className="form-input"
                  />
                </div>
              </div>
            </div>

            <h2>Inventory</h2>

            <div className="form-group">
              <label htmlFor="stock_status">Stock Status</label>
              <select
                id="stock_status"
                name="stock_status"
                value={formData.stock_status}
                onChange={handleChange}
                className="form-select"
              >
                <option value="instock">In Stock</option>
                <option value="outofstock">Out of Stock</option>
              </select>
            </div>

            <h2>Organization</h2>

            <div className="form-group">
              <label htmlFor="category">Category</label>
              <input
                type="text"
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g., Gaming, Hawaiian, T-shirts"
              />
              <small className="form-help">Separate multiple categories with commas</small>
            </div>

            <div className="form-group">
              <label htmlFor="tags">Tags</label>
              <input
                type="text"
                id="tags"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g., funny, trending, new"
              />
              <small className="form-help">Separate tags with commas</small>
            </div>

            <h2>Visibility</h2>

            <div className="form-group">
              <label htmlFor="visibility">Catalog Visibility</label>
              <select
                id="visibility"
                name="visibility"
                value={formData.visibility}
                onChange={handleChange}
                className="form-select"
              >
                <option value="visible">Visible</option>
                <option value="hidden">Hidden</option>
              </select>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="published"
                  checked={formData.published}
                  onChange={handleChange}
                />
                <span>Published (visible to customers)</span>
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="featured"
                  checked={formData.featured}
                  onChange={handleChange}
                />
                <span>Featured Product</span>
              </label>
            </div>

            {product?.printify_product_id && (
              <>
                <h2>Printify Sync</h2>
                <div className="form-group">
                  <button
                    type="button"
                    onClick={handleSyncFromPrintify}
                    className="btn-sync"
                    disabled={syncing}
                  >
                    {syncing ? 'Syncing...' : 'Sync from Printify'}
                  </button>
                  <small className="form-help">
                    Pull latest product data from Printify. Your categories, featured status, and published status will be preserved.
                  </small>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Product Images Preview */}
        {product?.images && product.images.length > 0 && (
          <div className="images-section">
            <h2>Product Images</h2>
            <div className="images-grid">
              {product.images.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`${product.name} ${index + 1}`}
                  className="product-image-preview"
                />
              ))}
            </div>
            <small className="form-help">Images are managed through Printify and cannot be edited here</small>
          </div>
        )}

        {/* Product Variations Info */}
        {product?.variation_count > 0 && (
          <div className="variations-section">
            <h2>Variations</h2>
            <p>This product has <strong>{product.variation_count} variations</strong> (colors/sizes).</p>
            <small className="form-help">Variations are managed through Printify</small>
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/admin/products')}
            className="btn-cancel"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-save"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>

      {/* Modal for confirmations and messages */}
      <ConfirmationModal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        actions={
          <>
            {modal.type === 'confirm' && (
              <>
                <button className="btn-cancel" onClick={closeModal}>
                  Cancel
                </button>
                <button className="btn-confirm" onClick={modal.onConfirm} disabled={syncing}>
                  {syncing ? 'Processing...' : 'Confirm'}
                </button>
              </>
            )}
            {(modal.type === 'success' || modal.type === 'error') && (
              <button className="btn-confirm" onClick={modal.onConfirm}>
                OK
              </button>
            )}
          </>
        }
      >
        {typeof modal.message === 'string' ? <p>{modal.message}</p> : modal.message}
      </ConfirmationModal>

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default AdminProductEdit;
